import type { EventApiResponse } from "@/lib/humhub-api/types";
import { formatEventListRow } from "@/lib/utils/v2/event-date";

export const pickPrimaryImageGuid = (
  files: EventApiResponse["content"]["files"] | undefined,
): string | null => {
  if (!files || files.length === 0) return null;

  const byCategory = files.find((file) => file.category === 1 && file.guid);
  if (byCategory?.guid) return byCategory.guid;

  const byMime = files.find(
    (file) => file.mime_type?.startsWith("image/") && file.guid,
  );
  if (byMime?.guid) return byMime.guid;

  return files.find((file) => file.guid)?.guid ?? null;
};

export const getShortTimeZoneName = (
  language: string,
  timeZone: string | undefined | null,
): string => {
  if (!timeZone) return "";
  try {
    const parts = new Intl.DateTimeFormat(language, {
      timeZone,
      timeZoneName: "short",
    }).formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? timeZone;
  } catch {
    return timeZone;
  }
};

export const buildEventDateLabel = (
  event: EventApiResponse,
  language: string,
  fallback: string,
): string => {
  if (!event?.start_datetime) return fallback;
  const baseLabel = formatEventListRow(event, { year: "auto" });
  const displayTimeZone =
    event.all_day === 1
      ? event.time_zone
      : Intl.DateTimeFormat().resolvedOptions().timeZone || event.time_zone;
  const tzLabel = getShortTimeZoneName(language, displayTimeZone);
  if (tzLabel && baseLabel.includes(tzLabel)) return baseLabel;
  return tzLabel ? `${baseLabel} (${tzLabel})` : baseLabel;
};
